import React from 'react';
import { BarChart3 } from 'lucide-react';
import { formatCrore } from '../../utils/issueSubscription';

const Tile = ({ label, value, note, tone = 'text-slate-900' }) => (
  <li className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 min-w-0">
    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 truncate">{label}</p>
    <p className={`mt-1 text-lg font-extrabold tabular-nums ${tone}`}>{value}</p>
    <p className="text-[10px] text-slate-400 truncate">{note}</p>
  </li>
);

// profile: utils/issueSubscription.js buildIssuerProfile (null when the issuer isn't in the catalog)
export const IssueFundamentals = ({ profile }) => {
  const kpis = profile?.kpis || [];
  const promoter = profile?.promoterHolding;
  const tiles = [
    ...kpis.map((kpi) => ({
      key: kpi.key,
      label: kpi.label,
      value: `${kpi.value.toFixed(2)}%`,
      note: `${kpi.note} · ${kpi.year}`,
      tone: kpi.value < 0 ? 'text-rose-600' : 'text-slate-900',
    })),
    profile?.peIssue && { key: 'pe', label: 'P/E at Issue Price', value: `${profile.peIssue.toFixed(2)}x`, note: 'Upper price band' },
    profile?.mcapPostCr && { key: 'mcap', label: 'Post-Issue M-Cap', value: formatCrore(profile.mcapPostCr), note: 'At the upper price band' },
  ].filter(Boolean);

  return (
    <section aria-labelledby="fundamentals-heading" className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 id="fundamentals-heading" className="flex items-center gap-2 text-sm sm:text-base font-extrabold text-slate-900">
            <BarChart3 className="w-4 h-4 text-[#008080]" aria-hidden="true" />
            Financial Health &amp; Fundamental Valuation
          </h2>
          <p className="mt-0.5 text-[11px] text-slate-500">Restated financial ratios from the offer document</p>
        </div>
        {kpis[0] && <span className="px-2 py-0.5 rounded-md border border-slate-200 bg-slate-50 text-[11px] font-bold text-slate-600">{kpis[0].year} Restated</span>}
      </div>

      {tiles.length ? (
        <ul className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
          {tiles.map(({ key, ...tile }) => (
            <Tile key={key} {...tile} />
          ))}
        </ul>
      ) : (
        <p className="mt-4 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-center text-xs text-slate-500">
          Financial ratios for this issuer haven’t been extracted from its offer document yet.
        </p>
      )}

      {promoter && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="flex flex-wrap items-baseline justify-between gap-2 text-xs">
            <p className="font-bold text-slate-800">Promoter Shareholding (Pre-Issue)</p>
            <p className="font-extrabold text-slate-900 tabular-nums">{promoter.total.toFixed(2)}%</p>
          </div>
          <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden" aria-hidden="true">
            <div className="h-full rounded-full bg-gradient-to-r from-[#006666] to-[#14b8a6]" style={{ width: `${promoter.total}%` }} />
          </div>
          <p className="mt-1.5 text-[11px] text-slate-500">
            {promoter.holders.map((holder) => `${holder.name} ${holder.percent.toFixed(2)}%`).join(' · ')}
          </p>
          <p className="mt-0.5 text-[10px] text-slate-400">Post-issue holding depends on the final fresh issue and offer for sale, confirmed in the prospectus.</p>
        </div>
      )}
    </section>
  );
};

export default IssueFundamentals;
